import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { meetingGuestKeys } from './use-meeting-guest-query'
import type {
    WorkActivityListParams,
    WorkActivityRequest,
    WorkActivityStatus,
} from '@/lib/api/work-activity-api'
import { workActivityApi } from '@/lib/api/work-activity-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

export const workActivityKeys = {
    all: ['work-activities'] as const,
    list: (params: WorkActivityListParams) =>
        [...workActivityKeys.all, 'list', params] as const,
    station: () => [...workActivityKeys.all, 'station'] as const,
}

/**
 * Bảng công việc của màn máy trạm.
 *
 * Máy trạm chạy suốt ca nên phải tự làm mới: SSE lo phần "có biến động thì nhảy
 * ngay", còn nhịp nền này bắt những thay đổi không sinh sự kiện (sửa giờ họp,
 * đổi người tham gia) và phủ luôn lúc SSE rớt kết nối.
 */
export function useStationWorkActivities(enabled = true) {
    return useQuery({
        queryKey: workActivityKeys.station(),
        queryFn: async () =>
            (await workActivityApi.listForStation()).data ?? [],
        enabled,
        refetchInterval: 60_000,
    })
}

export function useWorkActivities(params: WorkActivityListParams) {
    return useQuery({
        queryKey: workActivityKeys.list(params),
        queryFn: async () => (await workActivityApi.list(params)).data ?? [],
    })
}

/** Làm mới mọi danh sách sau khi ghi — board và lịch dùng chung endpoint. */
function useInvalidateWorkActivities() {
    const qc = useQueryClient()
    return () => {
        void qc.invalidateQueries({ queryKey: workActivityKeys.all })
        // Lưu lịch họp có thể dạy danh bạ chức vụ mới của một khách mời. Không
        // làm mới ở đây thì lần mở form kế tiếp vẫn gợi ý chức vụ cũ.
        void qc.invalidateQueries({ queryKey: meetingGuestKeys.all })
    }
}

export function useCreateWorkActivity() {
    const invalidate = useInvalidateWorkActivities()
    return useMutation({
        mutationFn: (body: WorkActivityRequest) => workActivityApi.create(body),
        onSuccess: () => {
            invalidate()
            toast.success(m.work_activity_saved())
        },
        onError: (err) => {
            const p = formatApiError(err, m.work_activity_save_error())
            toast.error(p.title, { description: p.description })
        },
    })
}

export function useUpdateWorkActivity() {
    const invalidate = useInvalidateWorkActivities()
    return useMutation({
        mutationFn: ({ id, body }: { id: string; body: WorkActivityRequest }) =>
            workActivityApi.update(id, body),
        onSuccess: () => {
            invalidate()
            toast.success(m.work_activity_saved())
        },
        onError: (err) => {
            const p = formatApiError(err, m.work_activity_save_error())
            toast.error(p.title, { description: p.description })
        },
    })
}

export function useDeleteWorkActivity() {
    const invalidate = useInvalidateWorkActivities()
    return useMutation({
        mutationFn: (id: string) => workActivityApi.remove(id),
        onSuccess: () => {
            invalidate()
            toast.success(m.work_activity_deleted())
        },
        onError: (err) => {
            const p = formatApiError(err, m.work_activity_delete_error())
            toast.error(p.title, { description: p.description })
        },
    })
}

/**
 * Kéo-thả một thẻ sang cột khác.
 *
 * Cố ý KHÔNG cập nhật lạc quan (optimistic): backend có thể từ chối vì quyền
 * (chỉ người giao việc mới đóng được) hoặc vì người khác vừa kéo trước (409).
 * Vẽ trước rồi rút lại làm người dùng tưởng đã lưu xong. Board tự giữ thẻ ở cột
 * cũ cho tới khi server trả lời.
 */
export function useChangeWorkActivityStatus() {
    const invalidate = useInvalidateWorkActivities()
    return useMutation({
        mutationFn: ({
            id,
            fromStatus,
            toStatus,
        }: {
            id: string
            fromStatus: WorkActivityStatus
            toStatus: WorkActivityStatus
        }) => workActivityApi.changeStatus(id, fromStatus, toStatus),
        onSuccess: invalidate,
        onError: (err) => {
            const p = formatApiError(err, m.work_activity_status_error())
            toast.error(p.title, { description: p.description })
            // Kéo hụt cũng phải nạp lại: 409 nghĩa là dữ liệu đang xem đã cũ.
            invalidate()
        },
    })
}
